/* ShowSecretRenderer.js */

"use strict";
const _ = require("lodash");
const uiCore = require("@gluenetworks/steve_ui_core");
const TextControl = uiCore.Control.Textfield.InputText;
const ButtonControl = uiCore.Control.Action.Button;

const MODAL_TITLE = "Add API Credential";

class ShowSecretRenderer {
    constructor(model) {
        /**
         * @type {AddLicenseComponent}
         */
        this._model = model;

        this._rootElem = this._createRootElement();

        this._clientId = new TextControl({
            darkTheme: true,
            readOnly: true,
        });
        this._copyClientId = document.createElement("base-icon-gl");
        this._copyClientId.dataset.icon = "common.duplicate";
        this._copyClientId.title = "Copy Client ID to clipboard";

        this._secret = new TextControl({
            darkTheme: true,
            readOnly: true,
        });
        this._copySecret = document.createElement("base-icon-gl");
        this._copySecret.dataset.icon = "common.duplicate";
        this._copySecret.title = "Copy Secret to clipboard";

        this._closeButton = new ButtonControl({label: "OK", darkTheme: true});
        this._onPasswordPreviewClick = this._onPasswordPreviewClick.bind(this);
        this._registerListeners();
        this.render();
    }

    get rootElement() {
        return this.getRootElement();
    }

    get model() {
        return this.getModel();
    }

    /*******************************************/
    /* START PUBLIC COMPONENT RENDERER METHODS */
    /*******************************************/
    render() {
        if (this._renderProm === null) {
            return Promise.resolve();
        }
        this._renderProm = this._renderProm || this._render();
        return this._renderProm;
    }

    shutdown() {
        if (this._shutdownProm === null) {
            return Promise.resolve();
        }
        this._shutdownProm = this._shutdownProm || this._shutdown();
        return this._shutdownProm;
    }

    getRootElement() {
        return this._rootElem;
    }

    getModel() {
        return this._model;
    }

    /********************************************/
    /* END PUBLIC COMPONENT RENDERER METHODS    */
    /* START PRIVATE COMPONENT RENDERER METHODS */
    /********************************************/

    _registerListeners() {
        this._closeButton.addListener(
            "UserClick",
            (this.close = this._model.closePopUp.bind(this._model))
        );
        this._copyClientId.addEventListener(
            "click",
            (this._copyClientIdClick = this._copyClientIdClick.bind(this))
        );
        this._copySecret.addEventListener(
            "click",
            (this._copySecretClick = this._copySecretClick.bind(this))
        );
    }

    _unregisterListeners() {
        this._closeButton.removeListener("UserClick", this.close);
        this._copyClientId.removeEventListener("click", this._copyClientIdClick);
        this._copySecret.removeEventListener("click", this._copySecretClick);
    }

    _registerCopyListener(button, handler) {
        button.addListener("UserClick", handler);
        this._model.once("Shutdown", () => {
            button.removeListener("UserClick", handler);
            button.shutdown();
        });
    }

    _render() {
        this._rootElem.innerHTML = "";
        const root = document.createDocumentFragment();
        const title = document.createElement("h2");

        const message = document.createElement("span");
        message.classList.add("message-area");
        message.innerText = `Important: Copy these credentials and store in a safe place. The secret will not be displayed again. To ensure the security of your account and data, do not share these credentials.\n
        If you lose this secret, delete the credential and create a new one.`;

        const copyWarningContainer = document.createElement("div");
        copyWarningContainer.classList.add("copy-warning-container");
        const copyWarning = document.createElement("base-icon-gl");
        copyWarning.dataset.icon = "common.warning";
        const passCopyWarning = document.createElement("span");
        passCopyWarning.classList.add("pass-copy-warning");
        passCopyWarning.innerText = `Copying the password to the clipboard is not secure. Make sure you use a trusted application and device.`;
        copyWarningContainer.appendChild(copyWarning);
        copyWarningContainer.appendChild(passCopyWarning);

        title.innerText = MODAL_TITLE;
        root.appendChild(title);
        root.appendChild(message);

        root.appendChild(this._createSection("Client ID", this._clientId, this._copyClientId));
        root.appendChild(this._createSection("Secret", this._secret, this._copySecret));

        root.appendChild(copyWarningContainer);

        root.appendChild(this._createActionsContainer());

        this._setValues();
        // this._setRevealedValue();
        return new Promise((resolve /*,reject*/) => {
            this._rootElem.appendChild(root);
            resolve();
        });
    }

    _setValues() {
        this._clientId.setValue(this._model._clientId);
        this._secret.setValue(this._model._secret);
    }

    _setRevealedValue() {
        const revealedSecret = _.get(
            this._model._credentialWorkResult,
            "summary.decryptedCreds.credentials._value",
            ""
        );
        this._secret.setValue(revealedSecret);

        this._clearTimeoutAndCreateNew();
    }

    _shutdown() {
        this._unregisterListeners();
        this._rootElem.remove();
        this._rootElem = null;

        return Promise.all([this._closeButton.shutdown()])
            .then(() => {
                this._model = null;
                this._shutdownProm = null;
                this._renderProm = null;
            })
            .catch((error) => {
                console.log(`${this.constructor.name}: error in shutdown`);
                console.error(error);
            });
    }

    /********************************************/
    /* END PRIVATE COMPONENT RENDERER METHODS   */
    /* START PRIVATE RENDERER METHODS           */
    /********************************************/

    _createRootElement() {
        const rootElement = document.createElement("div");
        rootElement.classList.add("show-secret", "modal_feedback_container");
        rootElement.component = this._model;
        return rootElement;
    }

    _createSection(name, Compnent, copyButton) {
        const section = document.createElement("section");
        const label = document.createElement("label");

        section.className = "key-section";
        label.innerText = name;
        Compnent.setAttribute("readonly", true);
        Compnent.setAttribute("rows", "6");

        section.appendChild(label);
        section.appendChild(Compnent.renderer.rootElement);

        if (copyButton) {
            section.appendChild(copyButton);
        }

        return section;
    }

    _onPasswordPreviewClick() {
        const input = this._secret._renderer._rootElem.querySelector("input");
        if (input.getAttribute("type") === "password") {
            this._setRevealedValue();
            input.setAttribute("type", "text");
            this._clearTimeoutAndCreateNew();
        } else {
            this._setValues();
            input.setAttribute("type", "password");
            clearTimeout(this._secretPreviewTimer);
        }
    }

    _createActionsContainer() {
        const container = document.createElement("section");
        container.className = "actions-container";
        container.appendChild(this._closeButton.renderer.rootElement);
        return container;
    }

    _clearTimeoutAndCreateNew() {
        clearTimeout(this._secretPreviewTimer);
        this._secretPreviewTimer = undefined;
        this._secretPreviewTimer = setTimeout(() => {
            this._setValues();
        }, 30000);
    }

    /********************************************/
    /* END PRIVATE RENDERER METHODS             */
    /* START HANDLER METHODS                    */
    /********************************************/
    /**/

    _copyClientIdClick() {
        const input = this._clientId._renderer._rootElem.querySelector("input");
        input.select();
        input.setSelectionRange(0, 99999); // For mobile devices

        // Copy the text inside the text field
        navigator.clipboard.writeText(input.value);
    }

    _copySecretClick() {
        const input = this._secret._renderer._rootElem.querySelector("input");
        input.select();
        input.setSelectionRange(0, 99999); // For mobile devices

        // Copy the text inside the text field
        navigator.clipboard.writeText(input.value);
    }

    /********************************************/
    /* END HANDLER METHODS                      */
    /* START PRIVATE API METHODS                */
    /********************************************/

    /*******************************************/
    /*  END PRIVATE API METHODS                */
    /* START PUBLIC API METHODS                */
    /********************************************/

    /*******************************************/
    /*  END PUBLIC API METHODS                */
    /*******************************************/
}

module.exports = ShowSecretRenderer;
